import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { LoginRequest } from '@/types';
import { apiService } from '@/services/api';
import { appStateService } from '@/services/appStateService';
import { notificationService } from '@/services/notificationService';

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Email non valida')
    .required('Email obbligatoria'),
  password: yup
    .string()
    .min(6, 'Password deve essere di almeno 6 caratteri')
    .required('Password obbligatoria'),
});

interface LoginFormProps {
  onLoginSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.login(data);
      
      // Salva token
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Aggiorna stato app
      appStateService.setAuthenticated(response.user);
      
      // Notifica successo
      notificationService.success(
        'Login effettuato',
        `Benvenuto ${response.user.firstName}!`
      );

      // Carica donatori in cache per lavoro offline
      try {
        console.log('📋 Loading donors for offline work...');
        notificationService.info('Caricamento', 'Caricamento elenco donatori per lavoro offline...');
        await apiService.loadAllDonorsToCache();
        console.log('✅ Donors loaded successfully');
        notificationService.success('Completato', 'Elenco donatori caricato con successo');
      } catch (donorError) {
        console.warn('⚠️ Failed to load donors cache:', donorError);
        notificationService.warning('Attenzione', 'Impossibile caricare l\'elenco donatori. La ricerca offline potrebbe non funzionare.');
        // Non bloccare il login se il caricamento donatori fallisce
      }
      
      onLoginSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Errore durante il login';
      setError(errorMessage);
      notificationService.error('Login fallito', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'background.default', // Usa il gradiente del tema
        padding: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/RB_Logo-Green.svg" 
                alt="Fondazione Roberta Mucera" 
                style={{ 
                  height: '80px', 
                  width: 'auto',
                  maxWidth: '100%'
                }} 
              />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
             
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Accedi per iniziare a raccogliere donazioni
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{ mb: 3 }}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mb: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Accedi'
              )}
            </Button>
          </form>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Solo per raccoglitori autorizzati
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
